import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import SiteFooter from '@/components/feature/SiteFooter';
import usePageSchema from '@/hooks/usePageSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://novaaiwebsites.com';

const aboutSchemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'About', item: `${siteUrl}/about` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${siteUrl}/about#webpage`,
    url: `${siteUrl}/about`,
    name: 'About NOVA AI DIGITAL | Veteran-Owned Web Agency Southern California',
    description: 'Learn about NOVA AI DIGITAL — a U.S. Navy veteran-owned web agency combining military discipline with AI-powered web design and automation for Southern California businesses.',
    inLanguage: 'en-US',
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: {
      '@type': 'Organization',
      '@id': `${siteUrl}/#business`,
      name: 'NOVA AI DIGITAL',
      description: 'Veteran-owned AI-powered web development agency in Southern California.',
      foundingDate: '2024',
      founder: {
        '@type': 'Person',
        name: 'Bryan Torres',
        description: 'U.S. Navy veteran and founder of NOVA AI DIGITAL.',
      },
      address: {
        '@type': 'PostalAddress',
        addressRegion: 'CA',
        addressCountry: 'US',
        addressLocality: 'Southern California',
      },
      areaServed: [
        { '@type': 'City', name: 'Los Angeles' },
        { '@type': 'City', name: 'Inland Empire' },
        { '@type': 'City', name: 'San Diego' },
        { '@type': 'City', name: 'High Desert' },
        { '@type': 'City', name: 'Riverside' },
        { '@type': 'City', name: 'Orange County' },
      ],
      sameAs: [
        'https://www.instagram.com/novaaidigitalllc/',
        'https://www.facebook.com/profile.php?id=61580586208643',
      ],
    },
  },
];

export default function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  usePageSchema(aboutSchemas, 'about');

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Hero heading */}
        <div className="h-20" />
        <div className="px-6 md:px-12 lg:px-16 pt-10 pb-8 md:pb-10">
          <h1
            className="text-black leading-none"
            style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(3.5rem, 9vw, 8rem)',
              letterSpacing: '-0.02em',
            }}
          >
            About Us
          </h1>
        </div>

        {/* Hero image */}
        <div className="w-full" style={{ height: 'clamp(280px, 45vw, 650px)' }}>
          <img
            src="https://static.wixstatic.com/media/11062b_a1e2f84dd2764ac29b8734cf5e98c865~mv2.jpg/v1/fill/w_1920,h_1080,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Office%20Meeting.jpg"
            alt="NOVA AI DIGITAL team at work"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Intro row */}
        <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 flex flex-col sm:flex-row items-start justify-between gap-8 border-b border-black/8">
          <p
            className="text-black/50 text-sm md:text-base leading-relaxed max-w-lg"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Veteran-owned and Southern California-based, NOVA AI DIGITAL builds
            high-converting websites and AI-powered digital solutions for local businesses.
          </p>
          <Link
            to="/contact"
            className="border border-black text-black text-xs uppercase tracking-widest px-8 py-4 hover:bg-black hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.14em' }}
          >
            Get in Touch
          </Link>
        </div>

        {/* Our Story */}
        <div className="px-6 md:px-12 lg:px-16 py-14 md:py-20 border-b border-black/8">
          <div className="flex flex-col md:flex-row gap-10 md:gap-20">
            <div className="md:w-1/3">
              <h2
                className="text-black leading-tight"
                style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}
              >
                Our Story
              </h2>
            </div>
            <div className="md:w-2/3 flex flex-col gap-5">
              <p className="text-black/60 text-sm md:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                NOVA AI DIGITAL was founded by a U.S. Navy veteran with a mission to bring enterprise-level
                digital solutions to small and medium-sized businesses across Southern California. After
                serving in the military, our founder recognized a gap — local businesses with incredible
                products and services, but outdated or non-existent digital presence.
              </p>
              <p className="text-black/60 text-sm md:text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                We combine the discipline and precision of military service with cutting-edge web design
                and AI automation technology to deliver results that matter: more leads, more appointments,
                and more revenue for our clients.
              </p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="px-6 md:px-12 lg:px-16 py-14 md:py-20 border-b border-black/8">
          <h2
            className="text-black mb-12"
            style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}
          >
            What We Stand For
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            {[
              { num: '01', title: 'Discipline', body: 'Military precision in every project. On-time delivery, clear communication, and zero compromise on quality.' },
              { num: '02', title: 'Innovation', body: 'Always at the forefront of web and AI technology so your business stays ahead of the competition.' },
              { num: '03', title: 'Results', body: 'We measure success by your growth — more leads, higher conversion rates, and stronger brand recognition.' },
            ].map(v => (
              <div key={v.num} className="py-10 md:py-12 pr-0 md:pr-12 border-b md:border-b-0 md:border-r border-black/8 last:border-0">
                <span className="text-xs text-black/30 mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>{v.num}</span>
                <h3 className="text-black mb-4" style={{ fontFamily: 'Syne, sans-serif', fontWeight: 600, fontSize: '1.2rem' }}>{v.title}</h3>
                <p className="text-black/50 text-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>{v.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Service areas */}
        <div className="px-6 md:px-12 lg:px-16 py-14 md:py-20">
          <div className="flex flex-col md:flex-row gap-10 md:gap-20">
            <div className="md:w-1/3">
              <h2
                className="text-black leading-tight"
                style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', letterSpacing: '-0.01em' }}
              >
                Service Areas
              </h2>
            </div>
            <div className="md:w-2/3">
              <p className="text-black/60 text-sm md:text-base leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Proudly serving businesses throughout Southern California and beyond — we work
                with clients nationwide via remote collaboration.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Los Angeles', 'Inland Empire', 'San Diego', 'High Desert', 'Riverside', 'Orange County', 'Nationwide Remote'].map(area => (
                  <span
                    key={area}
                    className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest text-black"
                    style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.1em' }}
                  >
                    {area}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
